import debugCreator from 'debug';

import {
  calculateDiscountAmount,
  calculateFreightAmount,
  processLineItems,
} from 'businessLogic/server/scsToIcnAdapter.js';
import { BankAccountTypeEnumInput } from 'components/Core/WalletForms/validations/src/types';
import convertLocaleToCountry from 'server/helpers/convertLocaleToCountry';
import { formatMaskedAccountNumber } from 'shared/PaymentHelpers';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import { isBatchPaymentsEnabled } from 'store/ixp/selector';
import { saleSelectors } from 'store/sale/selectors';
import { accountTypeTranslator, CpServerWalletHelpers } from 'store/wallet/helpers';

export interface PaymentPayload {
  tokenNumber: string;
  backupTokenNumber?: string;
  paymentMethod: string;

  [propName: string]: any;
}

const debug = debugCreator('store:payments:actions');

const generateSharedPayload = (state: any, isL3Included = false) => {
  const { companyInfo, insight, payment, sale } = state;
  const invoiceAmount = saleSelectors.amountSelector(sale);
  const balanceAmount = saleSelectors.balanceSelector(sale);
  const companyName = companyInfoSelectors.nameSelector(companyInfo);
  const companyLocale = companyInfoSelectors.localeSelector(companyInfo);
  const paymentProcessor = insightSelectors.paymentProcessorSelector(insight);
  const offerId = insightSelectors.offerIdSelector(insight);
  const currency = saleSelectors.currencySelector(sale);
  const transactionType = saleSelectors.typeSelector(sale);
  const customerName = saleSelectors.customerNameSelector(sale);
  const dutyAmount = saleSelectors.dutyAmountSelector(sale);
  const customerJobId = saleSelectors.customerJobIdSelector(sale);
  const customerJobName = saleSelectors.customerJobNameSelector(sale);

  const { id: invoiceId, lines, freight, tax = {} } = sale;
  const { subscriptionStatus } = companyInfo;
  const { appSourceOffering, isAvailableBalance, depositDisabled } = insight;

  const lineItems = processLineItems(lines);
  const discountAmount = calculateDiscountAmount(lines);
  const freightAmount = calculateFreightAmount(freight);
  const taxAmount = (tax && tax.totalTaxAmount) || 0;
  const shippingAmount = freightAmount;

  const { amount, partialPaymentEnabled, isDateScheduled, isAutoPayOn } = payment;
  const { isGpu: isGPU } = sale;

  // Payment Retry experiment eligibility flags
  // Used by server to determine if user should be bucketed for Payment Retry experiment
  const isBatchPayment = isBatchPaymentsEnabled(state);
  const isAmountEditable = partialPaymentEnabled && !isDateScheduled && !isAutoPayOn;

  let common: any = {
    companyLocale,
    paymentProcessor,
    amount,
    subscriptionStatus,
    offerId,
    currency,
    appSourceOffering,
    invoiceId,
    customerJobId,
    customerJobName,
    invoiceTotal: invoiceAmount,
    invoiceBalance: balanceAmount,
    isAvailableBalance,
    depositDisabled,
    transactionType,
    companyName,
    isGPU,
    customerName,
    // Payment Retry experiment eligibility
    isBatchPayment,
    isAmountEditable,
  };

  if (isL3Included) {
    common = {
      ...common,
      lineItems,
      freightAmount,
      discountAmount,
      shippingAmount,
      taxAmount,
      customerJobReferenceNumber: undefined,
      dutyAmount,
    };
  }

  return common;
};

export const getZipCode = (ccDetails: Record<string, any>) => {
  if (
    ccDetails &&
    ccDetails.address &&
    ccDetails.address.addressComponents &&
    ccDetails.address.addressComponents[0] &&
    ccDetails.address.addressComponents[0].name === 'postalCode'
  ) {
    return ccDetails.address.addressComponents[0].value;
  }
  return '';
};

/**
 * Extracts a specific address component value from ccDetails
 * @param ccDetails - Credit card details object containing address
 * @param componentName - Name of the address component to extract (e.g., 'streetAddress', 'city', 'state')
 * @returns The value of the address component or empty string if not found
 */
export const getAddressComponent = (
  ccDetails: Record<string, any>,
  componentName: string
): string => {
  if (ccDetails?.address?.addressComponents) {
    const component = ccDetails.address.addressComponents.find(
      (comp: { name: string; value?: string }) => comp.name === componentName
    );
    return component?.value || '';
  }
  return '';
};

/**
 * Translates Apple Pay details into CPServer format
 * @param {*} payment
 * @param {*} state
 * @returns {import('./cpServerTranslators').PaymentPayload}
 */
export const getApplePayPaymentPayloadForCpServer = (payment: Record<string, any>, state: any) => {
  const { sale, companyInfo } = state;
  const companyLocale = companyInfoSelectors.localeSelector(companyInfo);
  const customerName = saleSelectors.customerNameSelector(sale);
  const isGpu = saleSelectors.isGpu(sale);

  let body = {
    ...generateSharedPayload(state, true),
    cardType: 'Credit',
    name: customerName,
    maskedCC: payment.paymentMethod.displayName,
    tokenNumber: JSON.stringify({ token: payment }),
    paymentMethod: 'applePay',
    countryCode: convertLocaleToCountry(companyLocale),
    savePaymentMethod: false,
    isGpu,
  };

  validatePaymentPayload(body);
  return body;
};

/**
 * Translates Credit Card details into CPServer format
 * @param {*} ccDetails
 * @param {*} state
 * @returns {import('./cpServerTranslators').PaymentPayload}
 */
export const getCCPaymentPayloadForCpServer = (ccDetails: any, state: any) => {
  debug(ccDetails, state);

  const {
    wallet: { selectedWalletId, userWallets },
    payment: { isSavePaymentMethodChecked },
  } = state;

  let body: Record<string, any> = {
    ...generateSharedPayload(state, true),
  };

  if (userWallets && userWallets.length && selectedWalletId !== 'AddCard') {
    const card = userWallets.find((card: any) => card.id === selectedWalletId);

    const { cardNumber, cardType, payorFirstName, postalCode } = card;

    body = {
      ...body,
      name: payorFirstName,
      cardType: CpServerWalletHelpers.translateCardTypeToCpServer(cardType),
      maskedCC: cardNumber,
      tokenNumber: cardNumber,
      paymentMethod: 'credit card wallet',
      walletEntryId: selectedWalletId,
      zipCode: postalCode,
    };
  } else {
    const { number, cardType, name, token, backUpToken } = ccDetails;

    // For success screen, add some fields
    ccDetails.cardNumber = number;
    ccDetails.cardType = cardType;

    // Extract billing address fields
    const streetAddress = getAddressComponent(ccDetails, 'streetAddress');
    const city = getAddressComponent(ccDetails, 'city');
    const state = getAddressComponent(ccDetails, 'state');

    body = {
      ...body,
      tokenNumber: token,
      backupTokenNumber: backUpToken,
      paymentMethod: 'credit card',
      savePaymentMethod: isSavePaymentMethodChecked,
      maskedCC: formatMaskedAccountNumber(number),
      cardType: CpServerWalletHelpers.translateCardTypeToCpServer(cardType),
      name,
      zipCode: getZipCode(ccDetails),
      // Billing address fields
      ...(streetAddress && { streetAddress }),
      ...(city && { city }),
      ...(state && { state }),
    };
  }

  validatePaymentPayload(body);
  return body;
};

/**
 * Translates nanopay Bank payment details into CPServer format
 * @param {*} bankPaymentDetails
 * @param {*} state
 * @returns {import('./cpServerTranslators').PaymentPayload}
 */
export const getNanopayBankPaymentPayloadForCpServer = (
  nanopayDetails: Record<string, any>,
  state: any
) => {
  debug(nanopayDetails, state);

  let body = {
    ...generateSharedPayload(state, true),
  };

  body = {
    ...body,
    ...nanopayDetails,
    tokenNumber: `${nanopayDetails.nanopayToken}`,
    paymentMethod: 'BANKTRANSFER_CA',
  };

  validatePaymentPayload(body);
  return body;
};

export const getBankPaymentPayloadForCpServer = (bankDetails: Record<string, any>, state: any) => {
  const {
    wallet: { selectedWalletId, userWallets },
    payment: { isSavePaymentMethodChecked },
  } = state;

  let body: any = {
    ...generateSharedPayload(state),
  };

  if (userWallets && userWallets.length && selectedWalletId !== 'AddBank') {
    const bankAccount = userWallets.find((bank: any) => bank.id === selectedWalletId);

    const { accountNumber, accountType, payorFirstName, payorLastName } = bankAccount;

    body = {
      ...body,
      payorFirstName,
      payorLastName,
      accountNumber,
      accountType,
      displayAccountType: accountType,
      paymentMethod: 'bank wallet',
      walletEntryId: selectedWalletId,
    };
  } else {
    const { phone: phoneNumber, name, bankCode, ...restOfBankAccountInfo } = bankDetails;

    body = {
      ...body,
      ...restOfBankAccountInfo,
      paymentMethod: 'bank',
      routingNumber: bankCode,
      payorFirstName: name.split(' ')[0],
      payorLastName: !name.split(' ')[1] ? ' ' : name.split(' ')[1], //TODO trim the space in cp server
      phoneNumber,
      savePaymentMethod: isSavePaymentMethodChecked,
      displayAccountType: accountTypeTranslator.fromWalletToCpServer(
        restOfBankAccountInfo.accountType
      ),
      businessAccount:
        restOfBankAccountInfo.accountType === BankAccountTypeEnumInput.BUSINESS_CHECKING ||
        restOfBankAccountInfo.accountType === BankAccountTypeEnumInput.BUSINESS_SAVINGS,
    };
  }

  return body;
};

/**
 * Validator which throws if the payment payload is invalid
 * Meant to be used for credit card, debit card, and apple pay transactions
 * @param {import("./cpServerTranslators").PaymentPayload} body
 * @returns void
 */
export const validatePaymentPayload = (body: any) => {
  // this validation checks for walletTokenization tokenNumber used as a placeholder for the real credit card number. It should always be a string, and never null
  if (typeof body.tokenNumber !== 'string') {
    throw Error('invalid payment payload: tokenNumber should be a string');
  }
};
